import { DocValidityStatusEnum } from "../enums"
import { Profile } from "../models"
import { ProcessConfig } from "../models/processConfig/processConfigModel"
import { AssociationSettingsRepository, ProfileRepository } from "../repositories"
import {
  AssociationId,
  CohortName,
  DocName,
  FileName,
  ProcessConfigCategoryEnums,
  ProcessType,
  ProfileEventRecord,
  ProfilePk,
} from "../types"
import { ensureArrayFormat, removeUnderScores, toUpperCaseCustom, toValidPfDateObject } from "../utils"
import { DocValidityRecord } from "../models/docValidity/docValidityRecord"

const compulsoryFieldMap = {
  name: "Name",
  surname: "Surname",
  employeeNumber: "Clock Number",
  employmentStatus: "Employment Status",
  jobType: "Job Type",
  workSite: "Work Site",
  idPassport: "ID / Passport",
}

export class DocValidityService {
  buildPreRequisiteReport = (
    association: AssociationId,
    profileRepo: ProfileRepository,
    associationRepo: AssociationSettingsRepository,
    targetPks?: ProfilePk[],
  ) => {
    const pivotByRecord = (
      docAuditsPerPerson: Record<
        ProfilePk,
        {
          auditRecords: Record<string, any>[]
          profileData: Record<string, any>
        }
      >,
    ) => {
      return Object.values(docAuditsPerPerson).flatMap((entry) => {
        return entry.auditRecords.map((record) => ({ ...record, ...entry.profileData }))
      })
    }

    const processConfig = associationRepo.getProcessConfigEntity(association)
    const allPreRequisiteNames = processConfig.getAllPreRequisiteDocNames()

    let allProfileEntities = {}
    if (targetPks) {
      allProfileEntities = targetPks.reduce((acc, pk) => {
        const profileEntity = profileRepo.getProfileEntity(pk)
        if (!profileEntity) {
          return acc
        }
        acc[pk] = profileEntity
        return acc
      }, {} as Record<ProfilePk, Profile>)
    } else {
      allProfileEntities = profileRepo.getAllProfileEntities()
    }
    const groupedPreRequisiteRecords = this.packagePreRequisiteRecords(
      allProfileEntities,
      association,
      processConfig,
      allPreRequisiteNames,
      false,
    )

    return pivotByRecord(groupedPreRequisiteRecords).map((record) => new DocValidityRecord(record))
  }

  private packagePreRequisiteRecords = (
    allProfileEntities: Record<ProfilePk, Profile>,
    association: AssociationId,
    processConfig: ProcessConfig,
    allCompetencyNames: string[],
    ignoreJobType: boolean,
  ) => {
    const getPreRequisiteNames = (profileEntity: Profile, processConfigEntity: ProcessConfig) => {
      const cohorts = profileEntity.getCohorts()
      const generalData = profileEntity.getGeneralData()
      const combinedPreRequisiteNames = cohorts.reduce((acc: string[], cohort: CohortName) => {
        const segmentInfo = {
          ...generalData,
          division: cohort,
          cohort,
        }
        const fallBackValue: any[] = []
        const preRequisiteNames = processConfigEntity.getConfiguredProcessInfo(
          ProcessConfigCategoryEnums.requiredCompetencies,
          ProcessType.competencies,
          segmentInfo,
          fallBackValue,
        ) as DocName[]
        return [...acc, ...preRequisiteNames]
      }, [])
      return Array.from(new Set(combinedPreRequisiteNames))
    }

    const extractTargetProfileData = (profileEntity: Profile) => {
      let data: Record<string, any> = {}
      Object.keys(compulsoryFieldMap).forEach((field) => {
        const value = profileEntity.getGeneralDataValue(field, "-")
        data[field] = value
        if (field === "workSite") {
          data[field] = ensureArrayFormat(data[field])
        }
      })
      return data
    }

    const getExpiry = (eventRecord: ProfileEventRecord) => {
      const CANT_EXPIRE = ["ID", "CV"]
      const NONE_SET_STR = "NONE SET"
      const {
        competencyName = "",
        competencyExpiryDate,
        docValidityExpiryDate,
      } = eventRecord.rawData as Record<string, any>
      const expiryDateStr = docValidityExpiryDate || competencyExpiryDate

      function classifyExpiryDate(preRequisiteName: DocName, expiryUnixMs: number | undefined, currentUnixMs: number) {
        if (CANT_EXPIRE.includes(preRequisiteName)) {
          return DocValidityStatusEnum.VALID_CANT_EXPIRE
        }
        if (!expiryUnixMs) {
          return DocValidityStatusEnum.EXPIRED
        }
        if (expiryUnixMs < currentUnixMs) {
          return DocValidityStatusEnum.EXPIRED
        }
        const MS_PER_DAY = 86400000
        if (expiryUnixMs < currentUnixMs + 45 * MS_PER_DAY) {
          return DocValidityStatusEnum.EXPIRES_IN_45_DAYS
        }
        return DocValidityStatusEnum.VALID
      }
      let maybeValidExpiryDate = toValidPfDateObject(expiryDateStr)
      let expiryDate = NONE_SET_STR
      let expiryUnixMs = undefined
      if (maybeValidExpiryDate !== null) {
        expiryDate = expiryDateStr
        expiryUnixMs = maybeValidExpiryDate.getTime()
      }
      if (CANT_EXPIRE.includes(competencyName)) {
        expiryDate = NONE_SET_STR
      }
      const status = classifyExpiryDate(competencyName, expiryUnixMs, Date.now())
      return { status, expiryDate }
    }

    let groupedPreRequisiteAuditRecords = {} as Record<
      ProfilePk,
      { auditRecords: Record<string, any>[]; profileData: Record<string, any> }
    >
    Object.keys(allProfileEntities).forEach((profilePk) => {
      const profileEntity = allProfileEntities[profilePk]
      let targetPreRequisites = allCompetencyNames
      if (!ignoreJobType) {
        targetPreRequisites = getPreRequisiteNames(profileEntity, processConfig)
      }
      let auditRecords = []
      const targetProfileData = extractTargetProfileData(profileEntity)
      const cohorts = profileEntity.getCohorts() || []
      const allSortedProfileRecords = profileEntity.getAllProfileRecordsLabelled(association)
      const persistedDocs = profileEntity.getAssociationFileNames(association)

      for (const competencyName of targetPreRequisites) {
        const eventRecord = this.extractLatestRecordForPreRequisite(
          allSortedProfileRecords,
          competencyName,
          persistedDocs,
        )
        if (!eventRecord) {
          auditRecords.push({ competency: competencyName, status: DocValidityStatusEnum.NO_DOC })
          continue
        }
        const { status, expiryDate } = getExpiry(eventRecord)
        const statusSuffix = eventRecord.isPersisted ? "" : " (NOT SYNCED)"
        auditRecords.push({
          filename: eventRecord.docs?.[0] || "",
          competency: competencyName,
          status: `${status}${statusSuffix}`,
          expiryDate,
          // expiryDate: typeof expiryDate === "string" ? expiryDate : expiryDate.toISOString().split("T")[0],
        })
      }
      groupedPreRequisiteAuditRecords[profilePk] = {
        profileData: targetProfileData,
        auditRecords,
      }
    })
    return groupedPreRequisiteAuditRecords
  }

  private extractLatestRecordForPreRequisite = (
    allSortedProfileRecords: ProfileEventRecord[],
    preRequisiteName: string,
    persistedDocs: string[],
  ) => {
    /**
     * The profile.docs array lists filenames that have been persisted to
     * S3. The competency.docs array simply lists the files that are
     * associated with the competency. For now we want to ensure that the
     * competency.docs array is a subset of the profile.docs array.
     * i.e. we don't want to include competencies that have not been uploaded,
     * EVEN IF we have the competency record on file
     */

    function extractDocGenerationUnixMs(fileName: FileName) {
      const [generationUnixMs] = fileName.split("___")
      return parseInt(generationUnixMs)
    }

    function ensureValidDateActioned(rawData: Record<string, any>, fileName: FileName) {
      if (rawData.dateActioned == undefined) {
        rawData.dateActioned = `${extractDocGenerationUnixMs(fileName)}`
      }
      return rawData
    }

    function isPreRequisiteMatch(docTemplateName: DocName, fileName: FileName) {
      if (!fileName) {
        return false
      }
      const [, , ...docNameElements] = fileName.split("___")
      const docFileName = docNameElements.join("___")
      if (!docFileName) {
        return false
      }
      const docName = docFileName.split(".pdf")[0]
      return docTemplateName === docName
    }

    for (let record of allSortedProfileRecords) {
      let { rawData = {}, docs = [] } = record
      if (!Array.isArray(docs)) {
        continue
      }
      for (const docFileName of docs) {
        const isPersisted = persistedDocs.includes(docFileName)
        if (!isPersisted) {
          continue
        }
        if (isPreRequisiteMatch(preRequisiteName, docFileName)) {
          rawData = ensureValidDateActioned(rawData, docFileName)
          rawData.preRequisiteName = preRequisiteName
          return { ...record, rawData, docs: [docFileName], isPersisted } as ProfileEventRecord & {
            isPersisted: boolean
          }
        }
      }
    }
    return undefined
  }

  getAllPreRequisiteNames(docValidityRecordEntities: DocValidityRecord[]): DocName[] {
    let allPreRequisiteNames = [] as DocName[]
    docValidityRecordEntities.forEach((docValidityRecordEntity) => {
      const preRequisiteName = docValidityRecordEntity.getPreRequisiteName()
      if (!allPreRequisiteNames.includes(preRequisiteName)) {
        allPreRequisiteNames.push(preRequisiteName)
      }
    })
    allPreRequisiteNames.sort()
    return allPreRequisiteNames
  }

  getCategorisedDocsByDocType(docValidityRecordEntities: DocValidityRecord[]): Record<string, DocValidityRecord[]> {
    let categories = {} as Record<string, DocValidityRecord[]>
    docValidityRecordEntities.forEach((docValidityRecordEntity) => {
      const docType = docValidityRecordEntity.getPreRequisiteName()
      if (!categories[docType]) {
        categories[docType] = [] as DocValidityRecord[]
      }
      categories[docType].push(docValidityRecordEntity)
    })
    return categories
  }

  getCategorisedPreRequisiteStats(docValidityRecordEntities: DocValidityRecord[]): Record<string, any> {
    const categorisedDocs = this.getCategorisedDocsByDocType(docValidityRecordEntities)
    let docStats = {} as Record<string, any>
    Object.keys(categorisedDocs).forEach((docType) => {
      if (!docStats[docType]) {
        docStats[docType] = {}
      }
      categorisedDocs[docType].forEach((docValidityRecordEntity) => {
        let status = removeUnderScores(docValidityRecordEntity.getStatus())
        if (toUpperCaseCustom(status) === toUpperCaseCustom(DocValidityStatusEnum.VALID_CANT_EXPIRE)) {
          status = DocValidityStatusEnum.VALID
        }
        if (!docStats[docType][status]) {
          docStats[docType][status] = 0
        }
        docStats[docType][status] += 1
      })
    })
    return docStats
  }

  getCategorisedDocsByPerson(docValidityRecordEntities: DocValidityRecord[]): Record<string, DocValidityRecord[]> {
    let categories = {} as Record<string, DocValidityRecord[]>
    docValidityRecordEntities.forEach((docValidityRecordEntity) => {
      const { idPassport } = docValidityRecordEntity.getMetaData()
      if (!categories[idPassport]) {
        categories[idPassport] = [] as DocValidityRecord[]
      }
      categories[idPassport].push(docValidityRecordEntity)
    })
    return categories
  }

  getDocsInCategory(
    docValidityRecordEntities: DocValidityRecord[],
    category: DocValidityStatusEnum,
  ): DocValidityRecord[] {
    const categorisedDocs = this.getCategorisedDocsByCategory(docValidityRecordEntities)
    return categorisedDocs[category]
  }

  getCategorisedDocsByCategory(docValidityRecordEntities: DocValidityRecord[]): Record<string, DocValidityRecord[]> {
    let categories = {} as Record<string, DocValidityRecord[]>
    docValidityRecordEntities.forEach((docValidityRecordEntity) => {
      const status = docValidityRecordEntity.getStatus()
      if (!categories[status]) {
        categories[status] = [] as DocValidityRecord[]
      }
      categories[status].push(docValidityRecordEntity)
    })
    return categories
  }
}
